/**
 * Wrapper component for react-error-boundary with automatic error logging.
 * @module error/ErrorBoundaryWrapper
 */
import type { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { ErrorFallbackProps } from './ErrorFallback.tsx';
import { ErrorFallback } from './ErrorFallback.tsx';

export interface ErrorBoundaryWrapperProps {
  variant?: ErrorFallbackProps['variant'];
  title?: string;
  errorContext?: string;
  showRetry?: boolean;
  showDetails?: boolean;
  retryAction?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Wrapper around react-error-boundary with a configurable ErrorFallback.
 *
 * Renders ErrorFallback with the specified props.
 *
 * @example Basic usage
 * ```tsx
 * <ErrorBoundaryWrapper context="DashboardGrid">
 *   <DashboardGrid />
 * </ErrorBoundaryWrapper>
 * ```
 *
 * @example With metadata and retry
 * ```tsx
 * <ErrorBoundaryWrapper
 *   context="ChartRenderer"
 *   metadata={{ chartType: 'line', dataPoints: 50 }}
 *   title="Chart failed to render"
 *   errorContext="Type: Line | Rows: 50"
 *   showRetry
 *   showDetails
 * >
 *   <Chart />
 * </ErrorBoundaryWrapper>
 * ```
 *
 * @example Inline variant for grid cells
 * ```tsx
 * <ErrorBoundaryWrapper
 *   context="WidgetCell"
 *   variant="inline"
 *   title="Widget Error"
 * >
 *   <Widget />
 * </ErrorBoundaryWrapper>
 * ```
 */
export function ErrorBoundaryWrapper({
  variant,
  title,
  errorContext,
  showRetry,
  showDetails,
  retryAction,
  className,
  children,
}: ErrorBoundaryWrapperProps) {
  return (
    <ErrorBoundary
      FallbackComponent={(props) => (
        <ErrorFallback
          {...props}
          className={className}
          context={errorContext}
          retryAction={retryAction}
          showDetails={showDetails}
          showRetry={showRetry}
          title={title}
          variant={variant}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
